// Flow Configuration - Screen definitions and navigation
export const FLOW_CONFIG = {
  // Phase 1: Discovery and Personalization
  phase1: [
    {
      id: 'welcome',
      hash: '#welcome',
      template: 'screen-welcome',
      next: 'age',
      analytics: { screen_name: 'welcome' }
    },
    {
      id: 'age',
      hash: '#age',
      template: 'screen-age',
      next: 'skin-type',
      back: 'welcome',
      dataField: 'age_range',
      analytics: { screen_name: 'age_selection' }
    },
    {
      id: 'skin-type',
      hash: '#skin-type',
      template: 'screen-skin-type',
      next: 'primary-goal',
      back: 'age',
      dataField: 'skin_type',
      analytics: { screen_name: 'skin_type_selection' }
    },
    {
      id: 'primary-goal',
      hash: '#primary-goal',
      template: 'screen-primary-goal',
      next: 'additional-concerns',
      back: 'skin-type',
      dataField: 'primary_goal',
      analytics: { screen_name: 'primary_goal_selection' }
    },
    {
      id: 'additional-concerns',
      hash: '#additional-concerns',
      template: 'screen-additional-concerns',
      next: 'social-proof',
      back: 'primary-goal',
      dataField: 'additional_concerns',
      multiSelect: true,
      analytics: { screen_name: 'additional_concerns_selection' }
    },
    {
      id: 'social-proof',
      hash: '#social-proof',
      template: 'screen-social-proof',
      next: 'sensitivity',
      back: 'additional-concerns',
      analytics: { screen_name: 'social_proof_interstitial' }
    },
    {
      id: 'sensitivity',
      hash: '#sensitivity',
      template: 'screen-sensitivity',
      next: 'sun-behavior',
      back: 'social-proof',
      dataField: 'sensitivity_level',
      analytics: { screen_name: 'sensitivity_selection' }
    },
    {
      id: 'sun-behavior',
      hash: '#sun-behavior',
      template: 'screen-sun-behavior',
      next: 'routine-complexity',
      back: 'sensitivity',
      dataField: 'sun_behavior',
      analytics: { screen_name: 'sun_behavior_selection' }
    },
    {
      id: 'routine-complexity',
      hash: '#routine-complexity',
      template: 'screen-routine-complexity',
      next: 'current-products',
      back: 'sun-behavior',
      dataField: 'routine_complexity',
      analytics: { screen_name: 'routine_complexity_selection' }
    },
    {
      id: 'current-products',
      hash: '#current-products',
      template: 'screen-current-products',
      next: 'spf-usage',
      back: 'routine-complexity',
      dataField: 'current_products',
      multiSelect: true,
      analytics: { screen_name: 'current_products_selection' }
    },
    {
      id: 'spf-usage',
      hash: '#spf-usage',
      template: 'screen-spf-usage',
      next: 'makeup-frequency',
      back: 'current-products',
      dataField: 'spf_usage',
      analytics: { screen_name: 'spf_usage_selection' }
    },
    {
      id: 'makeup-frequency',
      hash: '#makeup-frequency',
      template: 'screen-makeup-frequency',
      next: 'medical',
      back: 'spf-usage',
      dataField: 'makeup_frequency',
      analytics: { screen_name: 'makeup_frequency_selection' }
    },
    {
      id: 'medical',
      hash: '#medical',
      template: 'screen-medical',
      next: 'ingredients',
      back: 'makeup-frequency',
      dataField: 'medical_considerations',
      multiSelect: true,
      analytics: { screen_name: 'medical_considerations_selection' }
    },
    {
      id: 'ingredients',
      hash: '#ingredients',
      template: 'screen-ingredients',
      next: 'budget',
      back: 'medical',
      dataField: 'excluded_ingredients',
      multiSelect: true,
      analytics: { screen_name: 'ingredient_exclusions_selection' }
    },
    {
      id: 'budget',
      hash: '#budget',
      template: 'screen-budget',
      next: 'location',
      back: 'ingredients',
      dataField: 'monthly_budget',
      analytics: { screen_name: 'budget_selection' }
    },
    {
      id: 'location',
      hash: '#location',
      template: 'screen-location',
      next: 'lifestyle',
      back: 'budget',
      dataField: 'location',
      analytics: { screen_name: 'location_input' }
    },
    {
      id: 'lifestyle',
      hash: '#lifestyle',
      template: 'screen-lifestyle',
      next: 'allergies',
      back: 'location',
      dataField: 'lifestyle_factors',
      multiSelect: true,
      analytics: { screen_name: 'lifestyle_factors_selection' }
    },
    {
      id: 'allergies',
      hash: '#allergies',
      template: 'screen-allergies',
      next: 'processing',
      back: 'lifestyle',
      dataField: 'allergies_intolerances',
      optional: true,
      analytics: { screen_name: 'allergies_input' }
    },
    {
      id: 'processing',
      hash: '#processing',
      template: 'screen-processing',
      next: 'email',
      duration: 8000, // 8 seconds
      analytics: { screen_name: 'processing_analysis' }
    }
  ],

  // Phase 2: Results Generation and Conversion
  phase2: [
    {
      id: 'email',
      hash: '#email',
      template: 'screen-email',
      next: 'selfie-intro',
      back: 'allergies',
      dataField: 'email',
      required: true,
      validation: 'email',
      analytics: { screen_name: 'email_capture' }
    },
    {
      id: 'selfie-intro',
      hash: '#selfie-intro',
      template: 'screen-selfie-intro',
      next: 'camera',
      back: 'email',
      analytics: { screen_name: 'selfie_introduction' }
    },
    {
      id: 'camera',
      hash: '#camera',
      template: 'screen-camera',
      next: 'analysis',
      back: 'selfie-intro',
      dataField: 'selfie',
      requiresCamera: true,
      analytics: { screen_name: 'camera_capture' }
    },
    {
      id: 'analysis',
      hash: '#analysis',
      template: 'screen-analysis',
      next: 'results',
      duration: 15000, // 15 seconds
      analytics: { screen_name: 'ai_analysis_loading' }
    },
    {
      id: 'results',
      hash: '#results',
      template: 'screen-results',
      next: 'plan',
      back: null,
      analytics: { screen_name: 'results_summary' }
    },
    {
      id: 'plan',
      hash: '#plan',
      template: 'screen-plan',
      next: 'prediction',
      back: 'results',
      analytics: { screen_name: 'action_plan_preview' }
    },
    {
      id: 'prediction',
      hash: '#prediction',
      template: 'screen-prediction',
      next: 'paywall',
      back: 'plan',
      analytics: { screen_name: 'goal_state_prediction' }
    }
  ],

  // Phase 3: Conversion and Paywall
  phase3: [
    {
      id: 'paywall',
      hash: '#subscription',
      template: 'screen-paywall',
      next: 'success',
      back: 'prediction',
      requiresPaddle: true,
      timer: 900, // 15 minutes in seconds
      analytics: { screen_name: 'paywall' }
    },
    {
      id: 'success',
      hash: '#success',
      template: 'screen-success',
      next: null,
      back: null,
      analytics: { screen_name: 'success' }
    }
  ],

  // Get all screens in order
  getAllScreens() {
    return [...this.phase1, ...this.phase2, ...this.phase3];
  },

  // Get screen by ID
  getScreenById(id) {
    return this.getAllScreens().find(screen => screen.id === id);
  },

  // Get screen by hash
  getScreenByHash(hash) {
    return this.getAllScreens().find(screen => screen.hash === hash);
  },

  // Get next screen
  getNextScreen(currentId) {
    const current = this.getScreenById(currentId);
    return current?.next ? this.getScreenById(current.next) : null;
  },

  // Get previous screen
  getPreviousScreen(currentId) {
    const current = this.getScreenById(currentId);
    return current?.back ? this.getScreenById(current.back) : null;
  },

  // Get total screen count
  getTotalScreens() {
    return this.getAllScreens().length;
  },

  // Get screen index
  getScreenIndex(id) {
    const screens = this.getAllScreens();
    return screens.findIndex(screen => screen.id === id) + 1;
  }
};